import { Mic, Square, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { uploadFile } from "@/lib/attachments";

/**
 * Records a voice note and uploads it.
 *
 * Mime type is negotiated rather than assumed — Safari does not produce
 * audio/webm, so hardcoding it yields an empty recording on Apple devices.
 */
const CANDIDATE_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return CANDIDATE_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
}

export function AudioRecorder({
  noteId,
  onRecorded,
  onCancel,
}: {
  noteId: string;
  onRecorded: (attachmentId: string, durationMs: number) => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<"idle" | "recording" | "uploading">("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const stream = useRef<MediaStream | null>(null);

  // Always release the microphone, including on unmount mid-recording.
  useEffect(
    () => () => {
      recorder.current?.state === "recording" && recorder.current.stop();
      stream.current?.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  useEffect(() => {
    if (state !== "recording") return;
    const t = setInterval(() => setSeconds(Math.floor((Date.now() - startedAt.current) / 1000)), 250);
    return () => clearInterval(t);
  }, [state]);

  const start = async () => {
    setError(null);
    try {
      const mimeType = pickMimeType();
      if (!mimeType) throw new Error("This browser cannot record audio.");

      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream.current, { mimeType });
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);

      rec.onstop = async () => {
        stream.current?.getTracks().forEach((t) => t.stop());
        const durationMs = Date.now() - startedAt.current;
        const blob = new Blob(chunks.current, { type: mimeType.split(";")[0] });
        setState("uploading");
        try {
          // Extension follows the negotiated container, not a hardcoded .webm —
          // Safari records mp4 and a wrong extension confuses downstream players.
          const ext = mimeType.includes("mp4") ? "m4a" : mimeType.includes("ogg") ? "ogg" : "webm";
          const id = await uploadFile(blob, "audio", {
            filename: `voice-${Date.now()}.${ext}`,
            noteId,
            durationMs,
          });
          onRecorded(id, durationMs);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
          setState("idle");
        }
      };

      recorder.current = rec;
      startedAt.current = Date.now();
      setSeconds(0);
      rec.start();
      setState("recording");
    } catch (e) {
      setError(
        e instanceof Error && e.name === "NotAllowedError"
          ? "Microphone access was denied. Allow it in your browser settings to record."
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  };

  const stop = () => recorder.current?.stop();

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-raised p-4">
      <div className="flex items-center gap-3">
        {state === "recording" ? (
          <>
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-tile-coral" />
            <span className="font-mono text-sm text-ink">
              {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
            </span>
            <Button size="sm" onClick={stop} className="ml-auto">
              <Square className="h-3.5 w-3.5" /> Stop
            </Button>
          </>
        ) : state === "uploading" ? (
          <span className="text-sm text-ink-muted">Uploading…</span>
        ) : (
          <>
            <Button size="sm" onClick={start}>
              <Mic className="h-4 w-4" /> Record a voice note
            </Button>
            <Button variant="ghost" size="sm" onClick={onCancel} className="ml-auto">
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {error ? <p className="text-sm text-tile-coral">{error}</p> : null}
    </div>
  );
}
